// src/pages/admin/HospitalManagement.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Info, AlertCircle, X, MapPin, Phone, Mail, Clock, Building2, Users } from 'lucide-react';
import Badge from '../../components/ui/Badge';
import Alert from '../../components/ui/Alert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DataTable from '../../components/tables/DataTable';
import SearchBar from '../../components/common/SearchBar';
import FilterDropdown from '../../components/common/FilterDropdown';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Pagination from '../../components/common/Pagination';
import usePermissions from '../../hooks/usePermissions';

const API_URL = 'https://medigo-api.onrender.com/api';

// Hospital Detail Modal Component
const HospitalDetailModal = ({ hospital, onClose }) => {
  if (!hospital) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Hospital Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900">{hospital.name}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant={hospital.isVerified ? 'success' : 'warning'}>
                    {hospital.isVerified ? 'Verified' : 'Pending Verification'}
                  </Badge>
                  <Badge variant={hospital.isActive ? 'success' : 'error'}>
                    {hospital.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-600" />
              Contact Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="text-gray-900 font-medium">{hospital.contact?.phone || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="text-gray-900 font-medium">{hospital.contact?.email || hospital.email || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Emergency</p>
                  <p className="text-gray-900 font-medium">{hospital.contact?.emergency || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <p className="text-gray-900 font-medium">
                    {hospital.location?.address || hospital.location?.city || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {hospital.description && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">About</h4>
              <p className="text-gray-700 leading-relaxed">{hospital.description}</p>
            </div>
          )}

          {/* Services */}
          {hospital.services && hospital.services.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Services Offered
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {hospital.services.map((service, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-800">{service.name || service}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Departments */}
          {hospital.departments && hospital.departments.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Departments</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {hospital.departments.map((dept, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <p className="font-medium text-gray-900">{dept.name || dept}</p>
                    {dept.description && (
                      <p className="text-sm text-gray-600 mt-1">{dept.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Capacity */}
          {hospital.capacity && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Capacity</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {hospital.capacity.totalBeds && (
                  <div className="text-center bg-blue-50 rounded-lg p-4">
                    <p className="text-2xl font-bold text-blue-600">{hospital.capacity.totalBeds}</p>
                    <p className="text-sm text-gray-600 mt-1">Total Beds</p>
                  </div>
                )}
                {hospital.capacity.icuBeds && (
                  <div className="text-center bg-red-50 rounded-lg p-4">
                    <p className="text-2xl font-bold text-red-600">{hospital.capacity.icuBeds}</p>
                    <p className="text-sm text-gray-600 mt-1">ICU Beds</p>
                  </div>
                )}
                {hospital.capacity.emergencyBeds && (
                  <div className="text-center bg-orange-50 rounded-lg p-4">
                    <p className="text-2xl font-bold text-orange-600">{hospital.capacity.emergencyBeds}</p>
                    <p className="text-sm text-gray-600 mt-1">Emergency Beds</p>
                  </div>
                )}
                {hospital.capacity.operationTheaters && (
                  <div className="text-center bg-purple-50 rounded-lg p-4">
                    <p className="text-2xl font-bold text-purple-600">{hospital.capacity.operationTheaters}</p>
                    <p className="text-sm text-gray-600 mt-1">Operation Theaters</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Operating Hours */}
          {hospital.operatingHours && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                Operating Hours
              </h4>
              {hospital.operatingHours.is24Hours ? (
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-green-800 font-semibold text-lg">Open 24/7</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(hospital.operatingHours).map(([day, hours]) => {
                    if (day === 'is24Hours' || !hours) return null;
                    return (
                      <div key={day} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                        <span className="font-medium text-gray-900 capitalize">{day}</span>
                        <span className="text-gray-600">
                          {hours.isClosed ? 'Closed' : `${hours.open || 'N/A'} - ${hours.close || 'N/A'}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Subscription Info */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h4>
            <div className="flex items-center justify-between">
              <div>
                <Badge variant={hospital.subscription ? 'success' : 'default'} size="lg">
                  {hospital.subscription ? 'Premium Plan' : 'Free Plan'}
                </Badge>
                {hospital.subscriptionExpiry && (
                  <p className="text-sm text-gray-600 mt-2">
                    Expires: {new Date(hospital.subscriptionExpiry).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <p>Created: {new Date(hospital.createdAt).toLocaleString()}</p>
              <p>Last Updated: {new Date(hospital.updatedAt).toLocaleString()}</p>
              {hospital.verificationStatus && (
                <p>Verification Status: <span className="font-medium">{hospital.verificationStatus}</span></p>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const HospitalManagement = () => {
  const {
    canViewHospitals,
    canApproveHospitals,
    canDeleteHospitals,
    canToggleHospitalStatus,
    isSuperAdmin,
    isAdmin
  } = usePermissions();

  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    isVerified: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    totalPages: 1
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailHospital, setDetailHospital] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (canViewHospitals) {
      fetchHospitals();
    }
  }, [filters, canViewHospitals]);

  const fetchHospitals = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('medigo_token');
      
      if (!token) {
        setError('Not authenticated. Please login again.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });
      params.append('_t', Date.now());

      const response = await axios.get(`${API_URL}/admin/hospitals?${params}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });

      setHospitals(response.data.data || []);
      setPagination({
        total: response.data.pagination?.total || 0,
        currentPage: response.data.pagination?.page || 1,
        totalPages: response.data.pagination?.pages || 1
      });
      setError('');
    } catch (err) {
      console.error('Error fetching hospitals:', err);
      setError(err.response?.data?.message || 'Failed to load hospitals');
      setHospitals([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHospitalDetails = async (hospitalId) => {
    try {
      setLoadingDetail(true);
      const token = localStorage.getItem('medigo_token');
      
      const response = await axios.get(`${API_URL}/admin/hospitals/${hospitalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDetailHospital(response.data.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error fetching hospital details:', err);
      setError(err.response?.data?.message || 'Failed to load hospital details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const verifyHospital = async (hospitalId) => {
    if (!canApproveHospitals) {
      setError('You do not have permission to verify hospitals');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      setActionLoading(hospitalId);
      const token = localStorage.getItem('medigo_token');
      
      const response = await axios.put(
        `${API_URL}/admin/hospitals/${hospitalId}/verify`, 
        {}, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(response.data.message || 'Hospital verified successfully');
      await fetchHospitals();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.message || 'Failed to verify hospital');
      setTimeout(() => setError(''), 3000);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleHospitalStatus = async (hospitalId) => {
    if (!canToggleHospitalStatus) {
      setError('You do not have permission to change hospital status');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      setActionLoading(hospitalId);
      const token = localStorage.getItem('medigo_token');
      
      const response = await axios.put(
        `${API_URL}/admin/hospitals/${hospitalId}/status`, 
        {}, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(response.data.message || 'Hospital status updated successfully');
      await fetchHospitals();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Status toggle error:', err);
      setError(err.response?.data?.message || 'Failed to update status');
      setTimeout(() => setError(''), 3000);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteHospital = async () => {
    if (!canDeleteHospitals) {
      setError('Only Super Admins can delete hospitals');
      setShowDeleteDialog(false);
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!selectedHospital?._id) {
      setError('Invalid hospital selected');
      setShowDeleteDialog(false);
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      setActionLoading(selectedHospital._id);
      const token = localStorage.getItem('medigo_token');
      
      const response = await axios.delete(
        `${API_URL}/admin/hospitals/${selectedHospital._id}`, 
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSuccess(response.data.message || 'Hospital deleted successfully');
      setShowDeleteDialog(false);
      setSelectedHospital(null);
      
      // If we're on the last item of a page, go back one page
      if (hospitals.length === 1 && filters.page > 1) {
        setFilters(prev => ({ ...prev, page: prev.page - 1 }));
      } else {
        await fetchHospitals();
      }
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.message || 'Failed to delete hospital');
      setTimeout(() => setError(''), 3000);
    } finally {
      setActionLoading(null);
      setShowDeleteDialog(false);
    }
  };

  const columns = [
    {
      header: 'Hospital',
      accessor: 'name',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <Building2 className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{row.name}</p>
            <p className="text-sm text-gray-600">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      header: 'Contact',
      accessor: 'phone',
      cell: (row) => (
        <div>
          <p className="text-sm text-gray-900">{row.contact?.phone || row.phone || 'N/A'}</p>
          <p className="text-xs text-gray-600">
            {row.location?.address || row.location?.city || 'No location'}
          </p>
        </div>
      ),
    },
    {
      header: 'Services',
      accessor: 'services',
      cell: (row) => (
        <span className="text-sm text-gray-600">
          {row.services?.length || 0} services
        </span>
      ),
    },
    {
      header: 'Subscription',
      accessor: 'subscription',
      cell: (row) => (
        <Badge variant={row.subscription ? 'success' : 'default'}>
          {row.subscription ? 'Subscribed' : 'Free'}
        </Badge>
      ),
    },
    {
      header: 'Status',
      accessor: 'isVerified',
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <Badge variant={row.isVerified ? 'success' : 'warning'}>
            {row.isVerified ? 'Verified' : 'Pending'}
          </Badge>
          <Badge variant={row.isActive ? 'success' : 'error'}>
            {row.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      ),
    },
    {
      header: 'Actions',
      accessor: 'actions',
      cell: (row) => {
        const isLoading = actionLoading === row._id;
        
        return (
          <div className="flex items-center gap-2">
            {/* View Details Button */}
            <button
              onClick={() => fetchHospitalDetails(row._id)}
              disabled={isLoading}
              className="text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="View Details"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
            
            {/* Verify */}
            {!row.isVerified && canApproveHospitals && (
              <button
                onClick={() => verifyHospital(row._id)}
                disabled={isLoading}
                className="text-green-600 hover:text-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Verify Hospital"
              >
                {isLoading ? (
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </button>
            )}
            
            {/* Toggle Status */}
            {canToggleHospitalStatus && (
              <button
                onClick={() => toggleHospitalStatus(row._id)}
                disabled={isLoading}
                className={`${row.isActive ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'} disabled:opacity-50 disabled:cursor-not-allowed`}
                title={row.isActive ? 'Deactivate' : 'Activate'}
              >
                {isLoading ? (
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : row.isActive ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </button>
            )}
            
            {/* Delete */}
            {canDeleteHospitals ? (
              <button
                onClick={() => {
                  setSelectedHospital(row);
                  setShowDeleteDialog(true);
                }}
                disabled={isLoading}
                className="text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete Hospital"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            ) : (
              <span className="text-gray-400 cursor-not-allowed" title="Only Super Admins can delete">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </span>
            )}
          </div>
        );
      },
    },
  ];

  if (!canViewHospitals) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading && hospitals.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            Hospital Management
            {isSuperAdmin && <Shield className="w-7 h-7 text-purple-600" />}
          </h1>
          <p className="text-gray-600 mt-1">Manage hospitals and medical facilities</p>
        </div>
      </div>

      {/* Permission Badge */}
      {isSuperAdmin ? (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center gap-3">
          <Shield className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div>
            <p className="text-purple-900 font-medium">Super Admin Privileges Active</p>
            <p className="text-purple-700 text-sm">
              You can verify, approve, and delete hospitals
            </p>
          </div>
        </div>
      ) : isAdmin ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-blue-900 font-medium">Admin Access</p>
            <p className="text-blue-700 text-sm">
              You can verify and approve hospitals. Only Super Admin can delete hospitals.
            </p>
          </div>
        </div>
      ) : null}

      {/* Alerts */}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SearchBar
            placeholder="Search hospitals..."
            onSearch={(value) => setFilters({ ...filters, search: value, page: 1 })}
            value={filters.search}
          />
          
          <FilterDropdown
            label="Verification Status"
            value={filters.isVerified}
            onChange={(value) => setFilters({ ...filters, isVerified: value, page: 1 })}
            options={[
              { label: 'All', value: '' },
              { label: 'Verified', value: 'true' },
              { label: 'Pending', value: 'false' },
            ]}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-blue-600">
                {pagination.total || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {hospitals.filter(h => !h.isVerified).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Verified</p>
              <p className="text-2xl font-bold text-green-600">
                {hospitals.filter(h => h.isVerified).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-blue-600">
                {hospitals.filter(h => h.isActive).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <DataTable
          columns={columns}
          data={hospitals}
          loading={loading}
        />
        
        {/* Pagination */}
        {!loading && hospitals.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              itemsPerPage={filters.limit}
              onPageChange={(page) => setFilters({ ...filters, page })}
              showItemsCount={true}
              showPageNumbers={true}
              maxVisiblePages={5}
              size="md"
            />
          </div>
        )}
      </div>

      {/* Hospital Detail Modal */}
      {showDetailModal && (
        <HospitalDetailModal
          hospital={detailHospital}
          onClose={() => {
            setShowDetailModal(false);
            setDetailHospital(null);
          }}
        />
      )}

      {/* Loading Detail Overlay */}
      {loadingDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6">
            <LoadingSpinner size="large" />
            <p className="text-gray-600 mt-4">Loading hospital details...</p>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
     <ConfirmDialog
  isOpen={showDeleteDialog}  
  onClose={() => {          
    setShowDeleteDialog(false);
    setSelectedHospital(null);
  }}
  onConfirm={handleDeleteHospital}
  title="Delete Hospital"
  message={`Are you sure you want to delete ${selectedHospital?.name}? This action cannot be undone.`}
  confirmText="Delete"
  cancelText="Cancel"
  type="danger"
/>
    </div>
  );
};

export default HospitalManagement;